// src/Components/ToastProvider.jsx
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { Snackbar, Alert } from "@mui/material";

const ToastContext = createContext();

let externalShowToast = null;

export const showGlobalToast = (message, severity = "info") => {
  externalShowToast?.(message, severity);
};

const severityConfig = {
  success: {
    bgColor: "#E8F7EE",
    borderColor: "#22C55E",
    textColor: "#166534",
    iconColor: "#16A34A",
  },
  error: {
    bgColor: "#FEECEC",
    borderColor: "#EF4444",
    textColor: "#991B1B",
    iconColor: "#DC2626",
  },
  warning: {
    bgColor: "#FFF7E6",
    borderColor: "#F59E0B",
    textColor: "#92400E",
    iconColor: "#D97706",
  },
  info: {
    bgColor: "#EAF3FF",
    borderColor: "#3B82F6",
    textColor: "#1E3A8A",
    iconColor: "#2563EB",
  },
};

export function ToastProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");

  const showToast = useCallback((msg, sev = "info") => {
    const safeSeverity = severityConfig[sev] ? sev : "info";
    setMessage(msg || "");
    setSeverity(safeSeverity);
    setOpen(true);
  }, []);

  useEffect(() => {
    externalShowToast = showToast;
    return () => {
      externalShowToast = null;
    };
  }, [showToast]);

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const config = severityConfig[severity] || severityConfig.info;

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      <Snackbar
        open={open}
        autoHideDuration={3200}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          zIndex: 99999999,
          top: { xs: 12, sm: 20 },
          width: "100%",
          maxWidth: { xs: "calc(100% - 24px)", sm: "420px" },
          mx: "auto",
          "& .MuiSnackbar-root": {
            zIndex: 99999999,
          },
        }}
      >
        <Alert
          severity={severity}
          onClose={handleClose}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "16px",
            px: 1.5,
            py: 1,
            display: "flex",
            alignItems: "center",
            backgroundColor: config.bgColor,
            color: config.textColor,
            border: `1px solid ${config.borderColor}`,
            boxShadow: "0 12px 30px rgba(0,0,0,0.14)",
            backdropFilter: "blur(10px)",
            direction: "rtl",
            "& .MuiAlert-icon": {
              color: config.iconColor,
              opacity: 1,
              fontSize: "1.35rem",
              alignItems: "center",
              padding: 0,
              mr: 1,
            },
            "& .MuiAlert-message": {
              padding: "2px 0",
              fontSize: "0.92rem",
              fontWeight: 600,
              lineHeight: 1.8,
              color: config.textColor,
            },
            "& .MuiAlert-action": {
              pr: 0,
              pl: 0.5,
              alignItems: "center",
              "& .MuiIconButton-root": {
                padding: "4px",
                color: config.textColor,
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.18)",
                },
              },
            },
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
